import validator from 'validator'
import { isValidE164Number } from '@super/shared-types'
import type {
  FieldValidator,
  FieldFormatter,
} from '@/components/common/ConditionBuilder'

/**
 * Email address validator
 * For "equals" operator, must be a valid email
 * For "contains/starts_with/ends_with", any string is valid
 */
export const emailValidator: FieldValidator = (value, operator) => {
  if (typeof value !== 'string') {
    return { valid: false, error: 'Must be a string' }
  }

  if (operator === 'equals' && !validator.isEmail(value)) {
    return { valid: false, error: 'Must be a valid email address' }
  }

  return { valid: true }
}

/**
 * Phone number validator - only digits and optional + prefix
 * Normalizes 10-digit US numbers to E.164 format (+1XXXXXXXXXX)
 */
export const phoneValidator: FieldValidator = value => {
  if (typeof value !== 'string') {
    return { valid: false, error: 'Must be a string' }
  }

  // Allow empty for partial entry
  if (!value) return { valid: true }

  // Must be only digits, optionally prefixed with +
  if (!/^\+?\d*$/.test(value)) {
    return {
      valid: false,
      error: 'Must contain only digits (and optional + prefix)',
    }
  }

  return { valid: true }
}

/**
 * Normalize phone number to E.164 format
 * - 10 digits → +1XXXXXXXXXX (US)
 * - Already has country code → keep as is
 */
export const normalizePhoneToE164 = (value: string): string => {
  if (!value) return value

  // Remove all non-digit characters except +
  const cleaned = value.replace(/[^\d+]/g, '')

  // If already starts with +, assume it's already in E.164 format
  if (cleaned.startsWith('+')) {
    return cleaned
  }

  // If exactly 10 digits, assume US number
  if (cleaned.length === 10) {
    return `+1${cleaned}`
  }

  // If 11 digits and starts with 1, it's a US number without +
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`
  }

  // Otherwise return as is
  return cleaned.startsWith('+') ? cleaned : cleaned ? `+${cleaned}` : cleaned
}

export interface ValidatedPhone {
  value: string
  extension?: string
}

export function splitPhoneExtension(raw: string): {
  phone: string
  extension?: string
} {
  const match = raw.trim().match(/^(.+?)\s*#\s*(\d+)$/)
  if (match) return { phone: match[1].trim(), extension: match[2].trim() }
  return { phone: raw.trim() }
}

export function validatePhoneE164(raw: string): ValidatedPhone | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const { phone, extension } = splitPhoneExtension(trimmed)
  const normalized = normalizePhoneToE164(phone)
  if (!isValidE164Number(normalized)) return null
  return extension ? { value: normalized, extension } : { value: normalized }
}

/**
 * Country code validator - only digits, optionally prefixed with +
 */
export const countryCodeValidator: FieldValidator = value => {
  if (typeof value !== 'string') {
    return { valid: false, error: 'Must be a string' }
  }

  // Allow empty for partial entry
  if (!value) return { valid: true }

  // Must be only digits, optionally prefixed with +
  if (!/^\+?\d*$/.test(value)) {
    return {
      valid: false,
      error: 'Must contain only digits (and optional + prefix)',
    }
  }

  return { valid: true }
}

/**
 * Phone number formatter
 * Formats E.164 phone numbers for display: +1 (999) 233-5292
 * Assumes value is already normalized to E.164 format
 */
export const phoneFormatter: FieldFormatter = {
  format: (value: string) => {
    if (!value) return ''

    // Normalize to E.164 first
    const normalized = normalizePhoneToE164(value)
    if (!normalized.startsWith('+')) return normalized

    // Remove the + for parsing
    const digits = normalized.slice(1)

    // Format based on country code and length
    // Assume country code 1 (US/Canada)
    if (digits.startsWith('1') && digits.length === 11) {
      // +1 (999) 233-5292
      const areaCode = digits.slice(1, 4)
      const firstPart = digits.slice(4, 7)
      const secondPart = digits.slice(7, 11)
      return `+1 (${areaCode}) ${firstPart}-${secondPart}`
    }

    // For other formats, just return the normalized E.164
    return normalized
  },

  parse: (value: string) => {
    // Remove all formatting, keep only digits and +
    return value.replace(/[^\d+]/g, '')
  },
}

// Format a stored/typed phone for display, preserving an optional #extension.
// Delegates to the shared `formatPhoneForDisplay` so admin, playground, and
// the node-graph summary panels all read the same output for the same input.
export { formatPhoneForDisplay as formatPhoneWithExtension } from '@super/shared-types'
